import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import { useApi } from "@/core/libs/api/react-query/useApi";
import { useAuth } from "@/core/libs/use-auth";

interface FetchResponse<T> {
  data?: T;
}

interface UseGetSettings {
  url: string;
  queryToken: string;
  params?: Record<string, number | string | boolean>;
  enabled?: boolean;
  errorMessage?: string;
  cacheTime?: number;
}

/**
 * Hook genérico para requisições GET autenticadas com React Query.
 * Utiliza `useApi` para requisições e gerencia cache automaticamente.
 *
 * @template T - Tipo esperado do retorno.
 * @param {string} url - Endpoint da API.
 * @param {string | string[]} queryToken - Chave do cache.
 * @param {Record<string, string | number | boolean>} params [params = { page: 1, limit: 10 }] - Query params.
 * @param {boolean} enabled [enabled=true] - Habilita a query.
 * @param {string} errorMessage [errorMessage = "Erro ao carregar dados"] - Mensagem de erro.
 * @param {number} cacheTime [cacheTime=Infinity] - Tempo de cache (ms).
 *
 * @example
 * const { data, isFetching } = useGetSettings<User[]>({
 *   url: "/users",
 *   queryToken: "users-list"
 * });
 */
export const useGet = <T = unknown>({
  url,
  queryToken,
  params = { page: 1, limit: 10 },
  enabled = true,
  errorMessage = "Erro ao carregar os dados",
  cacheTime = Infinity
}: UseGetSettings): {
  data: T;
  isFetching: boolean;
} => {
  const { execute } = useApi();
  const { isAuthenticated } = useAuth();

  const fetchData = useCallback(async (): Promise<FetchResponse<T>> => {
    try {
      return await execute<T>({
        url,
        params,
        messages: { error: errorMessage }
      });
    } catch (error) {
      console.error(`Erro em ${url}:`, error);
      throw error;
    }
  }, [execute, url, params, errorMessage]);

  const { data, isFetching } = useQuery<FetchResponse<T>, Error>({
    queryKey: Array.isArray(queryToken) ? queryToken : [queryToken, params],
    queryFn: fetchData,
    enabled: enabled && isAuthenticated,
    placeholderData: prev => prev,
    refetchOnWindowFocus: false,
    gcTime: cacheTime
  });

  return {
    data: data?.data,
    isFetching
  };
};
